import { useEffect } from 'react'
import { type AxiosError } from 'axios'
import {
  useGetUsersProfile,
  usePutUsersProfile,
  usePutUsersPassword,
} from '@/types/origin-point-engine'
import { logger } from '@/lib/logger'
import { type UserInfoResponse } from '../types/type'
import { useAuthStore } from '..'

export const useUser = () => {
  const { accessToken, reset } = useAuthStore(s => s.auth)
  const setUser = useAuthStore(s => s.auth.setUser)
  const { data: userProfile, refetch: refeshUserProfile } = useGetUsersProfile({
    query: {
      enabled: !!accessToken,
      select: (data: unknown) => {
        const response = data as UserInfoResponse
        return response.data.user
      },
      staleTime: 10 * 60 * 1000,
      // 错误处理：当用户不存在（404）时重置认证状态
      throwOnError: (error: Error) => {
        const axiosError = error as AxiosError
        if (axiosError.response?.status === 404) {
          logger.warn('用户资料不存在，重置认证状态')
          reset()
          return false // 不抛出错误，静默处理
        }
        return false // 其他错误也不抛出，避免阻塞 UI
      },
    },
  })
  // 将 API 返回的用户信息（含最新 role）同步回 auth store，确保侧边栏角色过滤正确
  useEffect(() => {
    if (userProfile) {
      setUser({
        id: userProfile.id,
        email: userProfile.email,
        role: userProfile.role,
        username: userProfile.username,
      })
    }
  }, [userProfile, setUser])

  const { mutateAsync: updateUserProfileAsync } = usePutUsersProfile({
    mutation: {
      onSuccess: () => {
        logger.debug('更新用户信息成功')
      },
      onError: (error) => {
        logger.error('更新用户信息失败', error)
      },
    },
  })

  const { mutateAsync: changePasswordAsync } = usePutUsersPassword({
    mutation: {
      onSuccess: () => {
        logger.debug('更新用户信息成功')
      },
      onError: (error) => {
        logger.error('更新用户信息失败', error)
      },
    },
  })

  return {
    userProfile,
    updateUserProfileAsync,
    refeshUserProfile,
    changePasswordAsync,
  }
}
